import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Icon from '../components/ui/icon';
import { useAuth } from '../contexts/AuthContext';
import { missionApi } from '../services/missionApi';
import { Mission, MissionSummary, getMissionStatusText, getMissionStatusColor, formatMissionPoints, isMissionAvailable, isMissionInProgress, isMissionCompleted, getDifficultyScoreText, getSuitabilityScoreText, getDifficultyScoreColor, getSuitabilityScoreColor, formatEstimatedTime, getRecommendationScoreText, getRecommendationScoreColor } from '../types/mission';

const Missions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [missions, setMissions] = useState<Mission[]>([]);
  const [missionStats, setMissionStats] = useState<MissionSummary | null>(null);
  const [aiMessage, setAiMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load missions data
  useEffect(() => {
    const loadMissions = async () => {
      if (!user?.outlet_id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Load all missions and stats
        const [allMissions, stats, message] = await Promise.all([
          missionApi.getAllMissions(user.outlet_id),
          missionApi.getMissionStats(user.outlet_id),
          missionApi.getMissionMessage(user.outlet_id, user.contact_person)
        ]);
        
        setMissions(allMissions);
        setMissionStats(stats);
        setAiMessage(message);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Không thể tải danh sách nhiệm vụ');
      } finally {
        setIsLoading(false);
      }
    };

    loadMissions();
  }, [user?.outlet_id, user?.contact_person]);

  // Handle mission actions
  const handleStartMission = (missionId: string) => {
    const message = `Hướng dẫn tôi tham gia để bắt đầu nhiệm vụ ${missionId} nhé!`;
    navigate('/assistant', { state: { initialMessage: message } });
  };

  const handleViewMission = (missionId: string) => {
    const message = `Cho tôi xem chi tiết nhiệm vụ ${missionId} nhé!`;
    navigate('/assistant', { state: { initialMessage: message } });
  };

  const handleContinueMission = (missionId: string) => {
    const message = `Tôi muốn tiếp tục nhiệm vụ ${missionId}, hỗ trợ nhé!`;
    navigate('/assistant', { state: { initialMessage: message } });
  };



  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-3">🌈 Nhiệm vụ</h1>
        <p className="text-md text-muted-foregroun px-4 text-muted-foreground">Tham gia nhiệm vụ để nhận điểm thưởng</p>
        
        {/* AI Message */}
        {aiMessage && (
          <div className="mt-4 p-4 bg-secondary rounded-lg">
            <p className="text-sm text-muted-foreground">{aiMessage}</p>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {missionStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 border-green-300 hover:border-green-500 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30"
            onClick={() => {
              if (missionStats.total_completed > 0) {
                navigate('/assistant', { 
                  state: { 
                    initialMessage: 'Cho tôi xem chi tiết các nhiệm vụ đã hoàn thành gần đây nhé!' 
                  } 
                });
              }
            }}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{missionStats.total_completed}</div>
              <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                Hoàn thành
                <Icon name="chevron-right" size={12} className="text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 border-blue-300 hover:border-blue-500 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
            onClick={() => {
              if (missionStats.total_points > 0) {
                navigate('/assistant', { 
                  state: { 
                    initialMessage: 'Điểm này đổi được những quà gì? Tôi muốn xem danh sách quà tặng có thể đổi nhé!' 
                  } 
                });
              }
            }}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{missionStats.total_points}</div>
              <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                Điểm tích lũy
                <Icon name="chevron-right" size={12} className="text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">{missionStats.available_to_start}</div>
              <div className="text-sm text-muted-foreground">Chưa tham gia</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{missionStats.completion_rate}%</div>
              <div className="text-sm text-muted-foreground">Tỷ lệ hoàn thành</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="alert-circle" size={20} className="text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-4">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                  <div className="h-10 bg-gray-200 rounded w-24"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Missions List */}
      {!isLoading && (
        <div className="space-y-4 pb-12">
          {missions.map((mission) => (
            <Card key={mission.mission_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg md:text-xl font-bold leading-tight">{mission.title}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap"> 
                        <Badge className={getMissionStatusColor(mission.participation_status.status)}>
                          {getMissionStatusText(mission.participation_status.status)}
                        </Badge>
                      </div>
                      <p className="text-md text-muted-foreground leading-relaxed">
                        {mission.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        <span className="text-muted-foreground">
                          ⏳ {formatEstimatedTime(mission.estimated_time)}</span>
                        <span className={getDifficultyScoreColor(mission.scoring.mds)}>
                          🫥 {getDifficultyScoreText(mission.scoring.mds)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        <span className={getSuitabilityScoreColor(mission.scoring.oss)}>
                          🫶🏻 {getSuitabilityScoreText(mission.scoring.oss)}
                        </span>
                        <span className={getRecommendationScoreColor(mission.scoring.mrs)}>
                          ✨ {getRecommendationScoreText(mission.scoring.mrs)}
                        </span>
                      </div>
                      {/* {mission.call_to_action && (
                        <p className="text-sm text-primary font-medium">
                          {mission.call_to_action}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Icon name="star" size={20} className="text-primary" />
                      <span className="font-bold text-primary text-lg">
                        {formatMissionPoints(mission.points_reward)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {isMissionAvailable(mission.participation_status.status) && (
                      <Button 
                        size="sm" 
                        className="bg-primary hover:bg-primary/90 text-sm px-3 py-2"
                        onClick={() => handleStartMission(mission.mission_id)}
                      >
                        <Icon name="play" size={16} className="mr-1" />
                        Bắt đầu
                      </Button>
                    )}
                    
                    {isMissionInProgress(mission.participation_status.status) && (
                      <>
                        <Button 
                          size="sm" 
                          variant="gray" 
                          className="text-sm px-3 py-2"
                          onClick={() => handleViewMission(mission.mission_id)}
                        >
                          <Icon name="eye" size={16} className="mr-1" />
                          Xem
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-blue-600 hover:bg-blue-700 text-sm px-3 py-2"
                          onClick={() => handleContinueMission(mission.mission_id)}
                        >
                          <Icon name="play" size={16} className="mr-1" />
                          Tiếp tục
                        </Button>
                      </>
                    )}
                    
                    {isMissionCompleted(mission.participation_status.status) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        disabled 
                        className="text-sm px-3 py-2"
                      >
                        <Icon name="check" size={16} className="mr-1" />
                        Hoàn thành
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && missions.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Icon name="target" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Chưa có nhiệm vụ</h3>
            <p className="text-muted-foreground">Hãy quay lại sau để xem nhiệm vụ mới</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Missions;